import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ErrorNote, Field, Input, Loading } from './ui';
import { api, type AIBudgetStatus } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

const usd = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A strip across the top of the app while the monthly AI budget is spent: the
 * optional background calls (summaries, embeddings) are stopped, and that must be
 * visible rather than something found out later (backlog #51). Renders nothing in
 * every other state, so it costs no space when there is nothing to say.
 */
export function AIBudgetBanner() {
  const budget = useApi<{ budget: AIBudgetStatus }>('/api/ai/budget');
  const status = budget.data?.budget;
  if (!status || status.level !== 'exceeded') return null;
  return (
    <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
      The monthly AI budget is spent ({usd(status.spent_usd)} of {usd(status.budget_usd ?? 0)}). Summaries and background
      search embeddings are paused; drafting and reply analysis still run.{' '}
      <Link to="/ai-usage" className="font-medium underline underline-offset-2">
        AI usage
      </Link>
    </div>
  );
}

/** The budget on the AI Usage page: spend against the limit, the warning or stop, and (for an admin) the setting. */
export function AIBudgetCard() {
  const { user } = useAuth();
  const budget = useApi<{ budget: AIBudgetStatus }>('/api/ai/budget');
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);

  const save = useAction(async (monthly: number | null) => {
    await api.put('/api/ai/budget', { monthly_usd: monthly });
    setEditing(false);
    budget.reload();
  });

  if (budget.loading && !budget.data) return <Loading label="Loading budget" />;
  if (budget.error) return <ErrorNote message={budget.error} onRetry={budget.reload} />;
  const status = budget.data?.budget;
  if (!status) return null;

  const percent = status.fraction === null ? null : Math.round(status.fraction * 100);
  const barTone = status.level === 'exceeded' ? 'bg-red-500' : status.level === 'warning' ? 'bg-amber-500' : 'bg-accent-500';

  return (
    <div className="rounded-lg border border-ink-200 p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-ink-950">Monthly budget</h2>
        {user?.role === 'admin' && !editing && (
          <Button
            size="sm"
            onClick={() => {
              setValue(status.budget_usd ? String(status.budget_usd) : '');
              setEditing(true);
            }}
          >
            {status.budget_usd ? 'Change' : 'Set a budget'}
          </Button>
        )}
      </div>

      {status.level === 'exceeded' && (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-inset ring-red-200">
          The budget is spent. Scheduled summaries and background search embeddings are <strong>stopped</strong>; drafting,
          reply analysis and anything you trigger yourself still run.
          {status.stop &&
            ` Since ${date(status.stop.first_at)}: ${status.stop.embeddings_skipped} timeline events were left without a search embedding, and summaries have not been refreshed.`}{' '}
          Raise the budget to resume. Events skipped meanwhile are not embedded afterwards.
        </p>
      )}
      {status.level === 'warning' && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-inset ring-amber-200">
          At {percent}% of the budget. At 100% the scheduled summaries and background embeddings stop; drafting and reply
          analysis keep running.
        </p>
      )}

      {status.budget_usd === null ? (
        <p className="text-sm text-ink-500">
          No budget is set, so nothing is capped here. The only limit is the one in the provider's console, which stops
          everything at once when it is hit. This month so far: {usd(status.spent_usd)} across all users and providers.
        </p>
      ) : (
        <>
          <div className="h-2 overflow-hidden rounded-full bg-ink-100">
            <div className={`h-full ${barTone}`} style={{ width: `${Math.min(100, percent ?? 0)}%` }} />
          </div>
          <p className="mt-2 text-sm text-ink-700">
            {usd(status.spent_usd)} of {usd(status.budget_usd)} ({percent}%) in {status.month}, across all users and
            providers. Counted by calendar month (UTC), like the provider's bill.
          </p>
        </>
      )}

      {editing && (
        <div className="mt-4 grid max-w-sm gap-3">
          {save.error && <ErrorNote message={save.error} />}
          <Field label="Monthly budget (USD)" hint="Set it a little under the limit in the provider's console. Leave blank to remove the cap.">
            <Input type="number" min="1" value={value} onChange={(event) => setValue(event.target.value)} placeholder="200" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" loading={save.pending} onClick={() => void save.run(value ? Number(value) : null)}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
